import { DateTime } from 'luxon';
import { getTariffCalculationEligibility } from '@billing/validation/tariffCalculationRules';
import { claimTariffStatusSchema } from '@billing/validation/tariff';
import type { Claim } from 'interfaces';
import { isClaimParentRow } from './claimTableRows';

const displayedTariffs = (row: Claim): Claim['tariffs'] =>
  isClaimParentRow(row)
    ? row.subRows.flatMap((child) => child.tariffs)
    : row.tariffs;

const latestTimestamp = (values: (string | null | undefined)[]): string => {
  const timestamps = values.flatMap((value) => {
    if (!value) return [];
    const parsed = DateTime.fromISO(value.replace(' ', 'T'), { zone: 'utc' });
    return parsed.isValid ? [parsed] : [];
  });
  timestamps.sort((left, right) => right.toMillis() - left.toMillis());
  return timestamps[0]?.toUTC().toISO() ?? '';
};

export const getProviderBilledDate = (row: Claim): string =>
  latestTimestamp(
    displayedTariffs(row).map((tariff) => tariff.lastSubmittedAt)
  );

export const getProviderRemittanceDate = (row: Claim): string => {
  const tariffIDs = new Set(displayedTariffs(row).map((tariff) => tariff.id));
  return latestTimestamp(
    (row.debits ?? [])
      .filter(
        (debit) =>
          tariffIDs.has(debit.tariffID) &&
          debit.payorID === row.payorID &&
          debit.type !== 'RETRACTION'
      )
      .map((debit) => debit.createdAt)
  );
};

export const getProviderOutstandingAmount = (row: Claim): number =>
  displayedTariffs(row).reduce((total, tariff) => {
    const status = claimTariffStatusSchema.safeParse(tariff.status);
    if (
      !status.success ||
      !getTariffCalculationEligibility(
        status.data,
        Boolean(tariff.lastSubmittedAt)
      ).outstanding
    )
      return total;
    const paid = (row.debits ?? [])
      .filter(
        (debit) => debit.tariffID === tariff.id && debit.payorID === row.payorID
      )
      .reduce((sum, debit) => sum + Number(debit.paidAmount ?? 0), 0);
    return total + Number(tariff.total ?? 0) - paid;
  }, 0);
